import { useMemo } from 'react';
import type { CustomFieldDef, CustomFieldValue, CustomFieldValueMap } from '@/api/customFields';
import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { FormField, Input, Textarea } from '@/components/ui/FormField';
import { SelectField } from './SelectField';

/**
 * Renders the per-practice custom fields catalog as a controlled form.
 * State is owned by the parent (ClientEditPage) so the same payload shape
 * can be merged into the PATCH body alongside the native ClientRecord
 * fields. The server runs its own validation on POST / PATCH — this
 * component mirrors the required / type rules so the user sees the error
 * client-side without a round trip, but the server is authoritative.
 *
 * Values are typed per def.type:
 *   text / textarea / select → string
 *   date                     → string 'YYYY-MM-DD'
 *   number                   → number (kept as string while editing, coerced on submit)
 *   multiselect              → string[]
 *   checkbox                 → boolean
 *
 * Errors are surfaced via FormField's `error` prop; we compute them from
 * the required flag + type rules on render. On submit the parent calls
 * `validate()` (exported alongside the component) and only dispatches
 * the PATCH if no errors remain.
 */

/** Local edit-state shape. Numbers stay as strings while the user types. */
export type CustomFieldFormState = Record<string, string | string[] | boolean>;

export function initCustomFieldsForm(
    defs: CustomFieldDef[],
    stored: Record<string, CustomFieldValue>,
): CustomFieldFormState {
    const out: CustomFieldFormState = {};
    for (const def of defs) {
        const raw = stored[def.id];
        switch (def.type) {
            case 'checkbox':
                out[def.id] = raw === true;
                break;
            case 'multiselect':
                out[def.id] = Array.isArray(raw) ? raw.slice() : [];
                break;
            case 'number':
                out[def.id] = raw === null || raw === undefined ? '' : String(raw);
                break;
            case 'date':
                out[def.id] = typeof raw === 'string' ? raw.slice(0, 10) : '';
                break;
            default:
                out[def.id] = typeof raw === 'string' ? raw : '';
        }
    }
    return out;
}

/**
 * Validates the current form state against the defs. Returns a map of
 * def.id → error message for any invalid fields, plus a `values` payload
 * ready to submit (native JSON types, empty strings dropped to null).
 * Number coercion happens here so the server sees a real number.
 */
export function validateCustomFieldsForm(
    defs: CustomFieldDef[],
    state: CustomFieldFormState,
): { errors: Record<string, string>; values: CustomFieldValueMap } {
    const errors: Record<string, string> = {};
    const values: CustomFieldValueMap = {};

    for (const def of defs) {
        const raw = state[def.id];
        let normalized: CustomFieldValue = null;

        switch (def.type) {
            case 'checkbox':
                normalized = raw === true;
                break;
            case 'multiselect': {
                const arr = Array.isArray(raw) ? raw : [];
                if (def.required && arr.length === 0) errors[def.id] = 'Required';
                normalized = arr.length ? arr : null;
                break;
            }
            case 'number': {
                const s = typeof raw === 'string' ? raw.trim() : '';
                if (!s) {
                    if (def.required) errors[def.id] = 'Required';
                    normalized = null;
                } else {
                    const n = Number(s);
                    if (!Number.isFinite(n)) errors[def.id] = 'Enter a valid number';
                    else normalized = n;
                }
                break;
            }
            case 'date': {
                const s = typeof raw === 'string' ? raw : '';
                if (!s) {
                    if (def.required) errors[def.id] = 'Required';
                    normalized = null;
                } else if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) {
                    errors[def.id] = 'Enter a valid date';
                } else {
                    normalized = s;
                }
                break;
            }
            case 'select': {
                const s = typeof raw === 'string' ? raw : '';
                if (!s) {
                    if (def.required) errors[def.id] = 'Required';
                    normalized = null;
                } else if (!def.options.includes(s)) {
                    errors[def.id] = 'Invalid option';
                } else {
                    normalized = s;
                }
                break;
            }
            default: {
                const s = typeof raw === 'string' ? raw : '';
                if (def.required && !s.trim()) errors[def.id] = 'Required';
                normalized = s.trim() ? s : null;
            }
        }
        values[def.id] = normalized;
    }

    return { errors, values };
}

interface CustomFieldsFormProps {
    defs: CustomFieldDef[];
    value: CustomFieldFormState;
    onChange: (next: CustomFieldFormState) => void;
    errors?: Record<string, string>;
    disabled?: boolean;
}

export function CustomFieldsForm({
    defs, value, onChange, errors, disabled,
}: CustomFieldsFormProps) {
    const sorted = useMemo(
        () => [...defs].sort((a, b) => a.sortOrder - b.sortOrder),
        [defs],
    );

    if (sorted.length === 0) {
        return (
            <Card>
                <CardHeader>
                    <div>
                        <CardTitle>Custom Fields</CardTitle>
                        <CardSubtitle>No custom fields configured for this practice.</CardSubtitle>
                    </div>
                </CardHeader>
                <p className="text-sm text-text-subtle">
                    Practice admins can add custom fields from Settings.
                </p>
            </Card>
        );
    }

    function setField(id: string, next: CustomFieldFormState[string]) {
        onChange({ ...value, [id]: next });
    }

    return (
        <Card>
            <CardHeader><CardTitle>Custom Fields</CardTitle></CardHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                {sorted.map((def) => (
                    <CustomFieldInput
                        key={def.id}
                        def={def}
                        value={value[def.id]}
                        error={errors?.[def.id]}
                        disabled={disabled}
                        onChange={(v) => setField(def.id, v)}
                    />
                ))}
            </div>
        </Card>
    );
}

function CustomFieldInput({
    def, value, onChange, error, disabled,
}: {
    def: CustomFieldDef;
    value: CustomFieldFormState[string];
    onChange: (next: CustomFieldFormState[string]) => void;
    error?: string;
    disabled?: boolean;
}) {
    switch (def.type) {
        case 'checkbox':
            return (
                <div className="mb-3.5 flex items-center gap-2">
                    <input
                        id={`cf-${def.id}`}
                        type="checkbox"
                        checked={value === true}
                        disabled={disabled}
                        onChange={(e) => onChange(e.target.checked)}
                        className="h-4 w-4 accent-accent"
                    />
                    <label
                        htmlFor={`cf-${def.id}`}
                        className="text-sm text-text select-none"
                    >
                        {def.label}
                        {def.required && <span className="text-error ml-0.5">*</span>}
                    </label>
                    {error && <span className="ml-2 text-xs text-error">{error}</span>}
                </div>
            );

        case 'textarea':
            return (
                <FormField
                    label={def.label}
                    required={def.required}
                    error={error}
                    className="sm:col-span-2"
                >
                    {(id) => (
                        <Textarea
                            id={id}
                            rows={3}
                            disabled={disabled}
                            value={typeof value === 'string' ? value : ''}
                            onChange={(e) => onChange(e.target.value)}
                        />
                    )}
                </FormField>
            );

        case 'date':
            return (
                <FormField label={def.label} required={def.required} error={error}>
                    {(id) => (
                        <Input
                            id={id}
                            type="date"
                            min="1900-01-01"
                            max="9999-12-31"
                            disabled={disabled}
                            value={typeof value === 'string' ? value : ''}
                            onChange={(e) => onChange(e.target.value)}
                        />
                    )}
                </FormField>
            );

        case 'number':
            return (
                <FormField label={def.label} required={def.required} error={error}>
                    {(id) => (
                        <Input
                            id={id}
                            type="number"
                            inputMode="decimal"
                            disabled={disabled}
                            value={typeof value === 'string' ? value : ''}
                            onChange={(e) => onChange(e.target.value)}
                        />
                    )}
                </FormField>
            );

        case 'select':
            return (
                <FormField label={def.label} required={def.required} error={error}>
                    {(id) => (
                        <SelectField
                            id={id}
                            disabled={disabled}
                            value={typeof value === 'string' ? value : ''}
                            onChange={(e) => onChange(e.target.value)}
                        >
                            <option value="">—</option>
                            {def.options.map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                            ))}
                        </SelectField>
                    )}
                </FormField>
            );

        case 'multiselect': {
            const selected = Array.isArray(value) ? value : [];
            return (
                <FormField label={def.label} required={def.required} error={error}>
                    {() => (
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 py-1">
                            {def.options.map((opt) => {
                                const checked = selected.includes(opt);
                                return (
                                    <label
                                        key={opt}
                                        className="inline-flex items-center gap-1.5 text-sm text-text select-none"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={disabled}
                                            onChange={(e) => {
                                                const next = e.target.checked
                                                    ? [...selected, opt]
                                                    : selected.filter((x) => x !== opt);
                                                onChange(next);
                                            }}
                                            className="h-4 w-4 accent-accent"
                                        />
                                        {opt}
                                    </label>
                                );
                            })}
                        </div>
                    )}
                </FormField>
            );
        }

        case 'text':
        default:
            return (
                <FormField label={def.label} required={def.required} error={error}>
                    {(id) => (
                        <Input
                            id={id}
                            disabled={disabled}
                            value={typeof value === 'string' ? value : ''}
                            onChange={(e) => onChange(e.target.value)}
                        />
                    )}
                </FormField>
            );
    }
}
